import React from 'react';

// Brand-logo assets. Anything not mapped here falls back to the text card.
//
// TODO: still missing logo files (add to src/images/brands/ + register below):
// StarTech.com.
import dellLogo from '../../images/brands/Dell_Logo.svg.png';
import hpLogo from '../../images/brands/Hewlett-Packard-Logo.wine.png';
import hpeLogo from '../../images/brands/Hewlett_Packard_Enterprise-Logo.wine.png';
import netgearLogo from '../../images/brands/Netgear-Logo.wine.png';
import smartLogo from '../../images/brands/SMART_Technologies_logo_richblack.jpg';
import samsungLogo from '../../images/brands/Samsung_Logo.svg.webp';
import sophosLogo from '../../images/brands/Sophos-Logo.wine.png';
import vertivLogo from '../../images/brands/Vertiv-logotyp.webp';
import ciscoLogo from '../../images/brands/cisco-logo.svg';
import kensingtonLogo from '../../images/brands/kensington-computer-products-group-vector-logo.png';
import trellixLogo from '../../images/brands/trellix-logo-png_seeklogo-510242.png';
// Reused from the Partners page asset set (already background-removed).
import viewsonicLogo from '../../images/partnes/WIth Removed Background/ViewSonic-logo-removebg-preview.png';
import lenovoLogo from '../../images/partnes/WIth Removed Background/lenovo-logo-brand-phone-symbol-name-red-design-china-mobile-illustration-free-vector-removebg-preview.png';
import apcLogo from '../../images/partnes/WIth Removed Background/apc3559.logowik.com-removebg-preview.png';
import targusLogo from '../../images/partnes/WIth Removed Background/Targus-Logo-Med-Purple-removebg-preview.png';
import pnyLogo from '../../images/partnes/WIth Removed Background/pny-technologies4162.logowik.com-removebg-preview.png';
import lexmarkLogo from '../../images/partnes/WIth Removed Background/Lexmark-Logo-removebg-preview.png';
// Brand logos dropped into src/images/brands/ (user-provided).
import asusLogo from '../../images/brands/ASUS.png';
import acerLogo from '../../images/brands/Acer.png';
import appleLogo from '../../images/brands/Apple.png';
import belkinLogo from '../../images/brands/Belkin.png';
import eatonLogo from '../../images/brands/Eaton.png';
import intelLogo from '../../images/brands/Intel.png';
import jabraLogo from '../../images/brands/Jabra.png';
import kingstonLogo from '../../images/brands/Kingston.png';
import lgLogo from '../../images/brands/LG.png';
import logitechLogo from '../../images/brands/Logitech.png';
import msiLogo from '../../images/brands/MSI.png';
import microsoftLogo from '../../images/brands/Microsoft.png';
import polyLogo from '../../images/brands/Poly.png';
import seagateLogo from '../../images/brands/Seagate.png';
import sonicwallLogo from '../../images/brands/SonicWall.png';
import ubiquitiLogo from '../../images/brands/Ubiquiti.png';
import westernDigitalLogo from '../../images/brands/WesternDigital.png';

// Keyed by lower-cased FIRST word of the manufacturer string. Ingram's
// vendor names sometimes suffix things like "HP Inc" or "HPE — Sourcing";
// the first-word key handles those variants without a separate map.
const BRAND_LOGO_MAP = {
    dell: dellLogo,
    hp: hpLogo,
    hpe: hpeLogo,
    'hewlett': hpLogo,          // "Hewlett Packard" → HP
    cisco: ciscoLogo,
    samsung: samsungLogo,
    netgear: netgearLogo,
    sophos: sophosLogo,
    vertiv: vertivLogo,
    smart: smartLogo,
    kensington: kensingtonLogo,
    trellix: trellixLogo,
    viewsonic: viewsonicLogo,
    lenovo: lenovoLogo,
    apc: apcLogo,              // "APC by Schneider Electric" → first word "apc"
    targus: targusLogo,
    pny: pnyLogo,
    lexmark: lexmarkLogo,
    asus: asusLogo,
    acer: acerLogo,
    apple: appleLogo,
    belkin: belkinLogo,
    eaton: eatonLogo,
    intel: intelLogo,          // Ingram "INTEL"
    jabra: jabraLogo,
    kingston: kingstonLogo,
    lg: lgLogo,
    logitech: logitechLogo,
    msi: msiLogo,
    microsoft: microsoftLogo,  // also catches "Microsoft Surface" (first word)
    poly: polyLogo,
    seagate: seagateLogo,
    sonicwall: sonicwallLogo,
    ubiquiti: ubiquitiLogo,
    western: westernDigitalLogo, // "Western Digital" → first word "western"
};

function pickLogo(brand, name) {
    const norm = (s) => String(s || '').trim().toLowerCase().split(/[\s\-|,]+/)[0];
    // 1) Try manufacturer's first word ("HP Inc" → "hp")
    const bkey = norm(brand);
    if (bkey && BRAND_LOGO_MAP[bkey]) return BRAND_LOGO_MAP[bkey];
    // 2) Fallback — first word of the PRODUCT NAME. Catches refurbishers /
    //    third-party resellers whose vendor field doesn't match the actual
    //    maker (e.g. "Joy Systems - Refurbished" selling "DELL 7070-MICRO...")
    const nkey = norm(name);
    if (nkey && BRAND_LOGO_MAP[nkey]) return BRAND_LOGO_MAP[nkey];
    return null;
}

/**
 * Clean fallback rendered whenever we don't have a real product photo.
 * If we have a brand logo image for the manufacturer, show that logo on a
 * neutral background. Otherwise show a text card with the brand name.
 *
 * Rule: if we're not confident about a real product image, show this —
 * never a possibly-wrong image and never an ugly external placeholder.
 */
export default function BrandPlaceholder({ brand, category, name, size = 'card' }) {
    const dims = {
        card:  { fontSize: '18px',  padding: '20px', gap: '8px',  subFont: '11px', logoMax: '55%' },
        hero:  { fontSize: '28px',  padding: '40px', gap: '12px', subFont: '14px', logoMax: '50%' },
        thumb: { fontSize: '12px',  padding: '10px', gap: '4px',  subFont: '9px',  logoMax: '65%' },
    }[size] || { fontSize: '18px', padding: '20px', gap: '8px', subFont: '11px', logoMax: '55%' };

    const logo = pickLogo(brand, name);

    const containerStyle = {
        width: '100%',
        height: '100%',
        minHeight: size === 'thumb' ? '60px' : '160px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: dims.gap,
        padding: dims.padding,
        background: 'linear-gradient(135deg, #f7f8fb 0%, #eceff5 100%)',
        borderRadius: '4px',
        textAlign: 'center',
        userSelect: 'none',
    };

    if (logo) {
        return (
            <div aria-label={`${brand} logo`} style={containerStyle}>
                <img
                    src={logo}
                    alt={`${brand} logo`}
                    style={{
                        maxWidth: dims.logoMax,
                        maxHeight: '55%',
                        objectFit: 'contain',
                        filter: 'grayscale(0.15)',
                        opacity: 0.9,
                    }}
                />
                {category && (
                    <span style={{ fontSize: dims.subFont, color: '#9ca3af', letterSpacing: '0.03em' }}>
                        {category}
                    </span>
                )}
            </div>
        );
    }

    // Text fallback — no brand logo asset available for this manufacturer.
    return (
        <div aria-label={`${brand || 'Product'} placeholder`} style={containerStyle}>
            <span
                style={{
                    fontSize: dims.fontSize,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    lineHeight: 1.1,
                    wordBreak: 'break-word',
                }}
            >
                {brand || 'Product'}
            </span>
            {category && (
                <span style={{ fontSize: dims.subFont, color: '#9ca3af', letterSpacing: '0.03em' }}>
                    {category}
                </span>
            )}
        </div>
    );
}
